"use client"

import { useEffect, useRef, useState } from "react"
import {
  ArrowUpRight,
  Sun,
  Moon,
  CloudSun,
  Cloud,
  CloudFog,
  CloudDrizzle,
  CloudRain,
  CloudSnow,
  CloudLightning,
  type LucideIcon,
} from "lucide-react"
import { useAuth } from "@/contexts/auth-context"
import { apiUrl, authHeaders } from "@/lib/api"
import { fireCouponConfetti } from "@/lib/coupon-confetti"

interface Weather { temp: number; code: number; isDay: boolean }
interface GeoLocation { city: string; region: string }
interface LastCoupon {
  id?: number
  codigo: string
  created_at: string
  discount_percentage?: number
  desconto?: string
  status?: string
  vantagem?: string
  store?: { name?: string } | null
}

interface CouponPreviewProps {
  coupon: LastCoupon
}

const STATUS_LABEL: Record<string, string> = {
  ativo: "ativo",
  embreve: "em breve",
  inativo: "inativo",
  expirado: "expirado",
  desconhecido: "—",
}

const STATUS_COLOR: Record<string, string> = {
  ativo: "text-emerald-600 dark:text-emerald-400",
  embreve: "text-amber-600 dark:text-amber-400",
  inativo: "text-muted-foreground",
  expirado: "text-rose-600 dark:text-rose-400",
  desconhecido: "text-muted-foreground",
}

function CouponChip({ coupon }: CouponPreviewProps) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const wrapperRef = useRef<HTMLSpanElement | null>(null)
  const closeTimer = useRef<number | null>(null)

  const openSoon = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
    setOpen(true)
  }
  const closeSoon = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpen(false), 160)
  }

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDocClick)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const status = coupon.status || "ativo"
  const discount = coupon.discount_percentage ? `−${coupon.discount_percentage}%` : coupon.desconto || ""
  const lojaNome = coupon.store?.name

  function copyCode(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    const trigger = e.currentTarget
    navigator.clipboard.writeText(coupon.codigo).then(() => {
      setCopied(true)
      fireCouponConfetti(trigger)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <span ref={wrapperRef} className="relative inline-block align-baseline" onMouseEnter={openSoon} onMouseLeave={closeSoon}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onFocus={openSoon}
        style={
          {
            "--shimmer-width": "70px",
            backgroundImage:
              "linear-gradient(90deg, hsl(var(--foreground)) 0%, hsl(var(--foreground)) 38%, var(--brand-amber) 50%, hsl(var(--foreground)) 62%, hsl(var(--foreground)) 100%)",
            backgroundSize: "calc(200% + 70px) 100%",
            backgroundRepeat: "no-repeat",
          } as React.CSSProperties
        }
        className="shimmer-link animate-shimmer-pan not-italic bg-clip-text font-mono text-[15px] font-medium tracking-tight text-transparent underline decoration-dotted decoration-1 underline-offset-[5px] transition-all focus:outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {coupon.codigo}
      </button>

      {open && (
        <div
          className="absolute left-0 top-[calc(100%+8px)] z-50 w-[300px] animate-fade-in not-italic"
          role="dialog"
        >
          <div className="rounded-xl border border-border/70 bg-popover/95 p-4 shadow-[0_18px_42px_-14px_rgba(0,0,0,0.18)] backdrop-blur-md dark:shadow-[0_18px_42px_-12px_rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[15px] font-semibold text-foreground">{coupon.codigo}</span>
              <button
                onClick={copyCode}
                className="rounded px-2 py-0.5 font-sans text-[11px] uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                aria-label="Copiar código"
              >
                {copied ? "copiado" : "copiar"}
              </button>
            </div>

            <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 font-sans text-xs">
              {discount && (
                <span className="font-mono text-sm font-semibold text-[var(--brand-amber)]">{discount}</span>
              )}
              {lojaNome && <span className="text-foreground/80">{lojaNome}</span>}
              <span className="text-border">·</span>
              <span className={`uppercase tracking-[0.14em] text-[10px] ${STATUS_COLOR[status] || STATUS_COLOR.ativo}`}>
                {STATUS_LABEL[status] || status}
              </span>
            </div>

            {coupon.vantagem && (
              <p className="mt-3 font-sans text-xs leading-relaxed text-muted-foreground">
                {coupon.vantagem}
              </p>
            )}

            <div className="mt-4 flex items-center justify-between border-t border-border/40 pt-3 font-sans">
              <span className="font-mono text-[11px] text-muted-foreground">
                capturado {timeSinceLong(coupon.created_at)}
              </span>
              <a
                href="/dashboard-cms/cupons"
                className="inline-flex items-center gap-1 text-xs font-medium text-foreground transition-colors hover:text-[var(--brand-amber)]"
              >
                Ver no kanban
                <ArrowUpRight className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      )}
    </span>
  )
}

function getGreeting(date: Date): string {
  const h = date.getHours()
  if (h < 5) return "Boa madrugada"
  if (h < 12) return "Bom dia"
  if (h < 18) return "Boa tarde"
  return "Boa noite"
}

function formatDateBR(d: Date): string {
  return d
    .toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })
    .replace(/^./, (c) => c.toUpperCase())
}

function formatTimeBR(d: Date): string {
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
}

function weatherIcon(code: number, isDay: boolean): { Icon: LucideIcon; label: string } {
  if (code === 0) return isDay ? { Icon: Sun, label: "céu limpo" } : { Icon: Moon, label: "noite limpa" }
  if (code === 1 || code === 2) return { Icon: CloudSun, label: "parcialmente nublado" }
  if (code === 3) return { Icon: Cloud, label: "nublado" }
  if (code === 45 || code === 48) return { Icon: CloudFog, label: "nevoeiro" }
  if (code >= 51 && code <= 57) return { Icon: CloudDrizzle, label: "garoa" }
  if (code >= 61 && code <= 67) return { Icon: CloudRain, label: "chuva" }
  if (code >= 71 && code <= 77) return { Icon: CloudSnow, label: "neve" }
  if (code >= 80 && code <= 82) return { Icon: CloudRain, label: "pancadas" }
  if (code >= 85 && code <= 86) return { Icon: CloudSnow, label: "neve" }
  if (code >= 95) return { Icon: CloudLightning, label: "tempestade" }
  return { Icon: Cloud, label: "—" }
}

function timeSinceLong(iso: string): string {
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60) return "há poucos segundos"
  const min = Math.floor(sec / 60)
  if (min < 60) return `há ${min} ${min === 1 ? "minuto" : "minutos"}`
  const h = Math.floor(min / 60)
  if (h < 24) return `há ${h} ${h === 1 ? "hora" : "horas"}`
  const d = Math.floor(h / 24)
  return `há ${d} ${d === 1 ? "dia" : "dias"}`
}

export default function DashboardHeader() {
  const { user } = useAuth()
  const [now, setNow] = useState<Date | null>(null)
  const [location, setLocation] = useState<GeoLocation | null>(null)
  const [weather, setWeather] = useState<Weather | null>(null)
  const [lastCoupon, setLastCoupon] = useState<LastCoupon | null>(null)

  useEffect(() => {
    setNow(new Date())
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    let cancelled = false

    // A frase "Último código capturado X há Y minutos" combina um relógio vivo
    // com este dado. Sem refetch, o Y crescia sobre um cupom congelado no mount
    // e a operadora concluía que o bot morreu. Mesmo ritmo do pulse da navbar.
    function fetchLastCoupon() {
      const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
      fetch(`${apiUrl()}/coupons/?ordering=-created_at&page_size=1`, { headers: authHeaders(token) })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (cancelled || !data) return
          const lista = data.results || (Array.isArray(data) ? data : [])
          if (lista.length > 0 && lista[0].codigo && lista[0].created_at) {
            setLastCoupon(lista[0])
          }
        })
        .catch(() => {})
    }

    fetchLastCoupon()
    const id = setInterval(fetchLastCoupon, 60_000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const geoRes = await fetch("https://ipapi.co/json/")
        if (!geoRes.ok) return
        const geo = await geoRes.json()
        if (cancelled) return
        if (geo.city) {
          setLocation({ city: geo.city, region: geo.region_code || "" })
        }
        if (geo.latitude && geo.longitude) {
          const wRes = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${geo.latitude}&longitude=${geo.longitude}&current=temperature_2m,weather_code,is_day&timezone=auto`
          )
          if (!wRes.ok) return
          const w = await wRes.json()
          if (cancelled) return
          if (w.current) {
            setWeather({
              temp: Math.round(w.current.temperature_2m),
              code: w.current.weather_code ?? 0,
              isDay: w.current.is_day === 1,
            })
          }
        }
      } catch {
        // Geo/weather são opcionais e dependem de APIs externas (ipapi.co,
        // open-meteo). Quando falham (CORS, rate limit, offline), o header
        // simplesmente não mostra cidade/temperatura — não vale tratar
        // como issue. console.warn aqui vira "1 issue" no Next 16 dev
        // overlay e poluía o dev experience.
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const greeting = now ? getGreeting(now) : "Olá"
  const firstName = user?.first_name?.split(" ")[0] || user?.username || ""
  const wInfo = weather ? weatherIcon(weather.code, weather.isDay) : null

  return (
    <header className="animate-stagger-in flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="font-display text-4xl font-normal tracking-[-0.015em] text-foreground sm:text-6xl">
          {greeting}
          {firstName && (
            <>
              , <span className="italic">{firstName}</span>
            </>
          )}
          .
        </h1>

        <p className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-sm text-foreground/70">
          {now ? (
            <>
              <span>{formatDateBR(now)}</span>
              <span className="text-border">·</span>
              <span>{formatTimeBR(now)}</span>
              {/* Cidade e clima são enfeite — no celular disputariam a primeira
                  dobra com o dado de negócio, então só aparecem de sm pra cima. */}
              {location && (
                <span className="hidden sm:inline">
                  <span className="text-border">· </span>
                  <span>{location.city}{location.region ? ` ${location.region}` : ""}</span>
                </span>
              )}
              {weather && wInfo && (
                <span className="hidden sm:inline-flex items-baseline gap-1.5">
                  <span className="text-border">·</span>
                  <span className="inline-flex items-center gap-1.5" title={wInfo.label}>
                    <wInfo.Icon className="h-4 w-4 text-[var(--brand-amber)]" />
                    <span>{weather.temp}°</span>
                  </span>
                </span>
              )}
            </>
          ) : (
            <span className="inline-block h-5 w-64 animate-pulse rounded bg-muted/60" />
          )}
        </p>

        {lastCoupon && now && (
          <div className="mt-4 flex max-w-xl flex-wrap items-baseline gap-x-2 gap-y-1 font-display text-base italic text-foreground/80">
            <span>Último código capturado</span>
            <CouponChip coupon={lastCoupon} />
            <span>{timeSinceLong(lastCoupon.created_at)}.</span>
          </div>
        )}
      </div>

      <nav className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        <a href="/dashboard-cms/saude" className="group inline-flex items-center gap-1.5 text-sm font-medium text-foreground transition-colors hover:text-foreground/70">
          Monitor de saúde
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
        <a href="/dashboard-cms/realtime" className="group inline-flex items-center gap-1.5 text-sm font-medium text-foreground transition-colors hover:text-foreground/70">
          Tempo real
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </nav>
    </header>
  )
}
